import { read, utils, writeFileXLSX } from 'xlsx';
import { saveAs } from 'file-saver';

import { toasterEmitter } from '@wf/components';

import { itemsSheetName } from '../modules/License/constants';

import { FileType } from '../constants';

import i18n from '../configs/i18n';

export const toasterError = ({ title = 'Error', description }: { title?: string; description?: string }) => {
  toasterEmitter({
    title: i18n.t(title) as string,
    status: 'error',
    description: i18n.t(description) as string,
    type: 'dark',
  });
};

export const toasterWarning = ({
  title = 'Warning',
  messageParams = {},
  description,
}: {
  title?: string;
  description?: string;
  messageParams?: Record<string, string | number>;
}) => {
  toasterEmitter({
    title: i18n.t(title) as string,
    status: 'warning',
    description: i18n.t(description, messageParams) as string,
    type: 'dark',
  });
};

export const toasterSuccess = ({ title, description }: { title: string; description?: string }) => {
  toasterEmitter({
    title: i18n.t(title) as string,
    status: 'success',
    type: 'dark',
    position: 'bottom-center',
    ...(description && { description: i18n.t(description) as string }),
  });
};

export const toasterInfo = ({ title, description }: { title: string; description?: string }) => {
  toasterEmitter({
    title: i18n.t(title) as string,
    status: 'info',
    description: i18n.t(description) as string,
    type: 'dark',
    ...(description && { description: i18n.t(description) as string }),
  });
};

export const toasterValidationError = (errors: unknown) => {
  if (!Array.isArray(errors)) {
    return;
  }

  errors.forEach(({ messageCode }) => {
    if (messageCode) {
      toasterError({ description: messageCode });
    }
  });
};

export function downloadFile(data: Blob | string, filename: string, mime?: string) {
  const blob = new Blob([data], { type: mime || 'application/octet-stream' });
  saveAs(blob, filename);
}

export function toCapitalize(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
}

export function splitCamelCase(str: string) {
  return str
    .split(/([A-Z][a-z]+)/)
    .filter(Boolean)
    .map(toCapitalize)
    .join(' ');
}

export function queryStringToObject(url: string) {
  return [...new URLSearchParams(url.split('?')[1])].reduce(
    (a, [k, v]) => ((a[k] = v), a),
    {} as Record<string, string>
  );
}

export async function importExcelFile(file: File): Promise<Record<string, Record<string, unknown>[]>> {
  const ab = await file.arrayBuffer();
  const workBook = read(ab);

  const importedData: Record<string, Record<string, string>[]> = {};

  workBook.SheetNames.forEach((sheetName) => {
    const workSheet = workBook.Sheets[sheetName]!;
    const key =
      workBook.SheetNames.length === 1 || sheetName.toLowerCase().includes(itemsSheetName.toLowerCase())
        ? itemsSheetName
        : sheetName;
    importedData[key] = utils.sheet_to_json<Record<string, string>>(workSheet);
  });

  return importedData;
}

export function exportExcelFile(sheets: { data: string[][]; sheetName: string }[], fileName: string) {
  const workBook = utils.book_new();

  sheets.forEach(({ data, sheetName }) => {
    const workSheet = utils.aoa_to_sheet(data);
    utils.book_append_sheet(workBook, workSheet, sheetName);
  });

  writeFileXLSX(workBook, `${fileName}.xlsx`);
}

export const getIdMap = <T extends { id: string | number }>(data: T[]) => {
  const idMap = {} as Record<string, T>;
  data.forEach((item) => (idMap[item.id] = item));

  return idMap;
};

export function getIconName(fileName: string) {
  const extension = fileName.split('.').pop()?.toLowerCase();

  switch (extension) {
    case 'pdf':
      return 'ic_pdf';
    case 'jpg':
    case 'jpeg':
      return 'ic_jpg';
    case 'png':
      return 'ic_png';
    default:
      return '';
  }
}

export const getFileType = (file: File) => {
  const mimeType = file.type.toLowerCase();

  switch (mimeType) {
    case 'image/jpeg':
    case 'image/jpg':
      return FileType.JPEG;
    case 'image/png':
      return FileType.PNG;
    case 'application/pdf':
      return FileType.PDF;
    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
      return FileType.XLSX;
    default:
      return null;
  }
};
